import { useGameStore } from '@/state/gameStore';

/**
 * Phase 15.3 — Beta-Logger. Hält die letzten 50 console-/Error-Events in einem
 * Ringpuffer, sendet bei {@link __IS_BETA__} `console.error` + uncaught Errors
 * automatisch an die Cloudflare-Pages-Function `/api/log`. Plus eine
 * {@link submitBugReport}-Funktion für vom User getriggerte Bug-Reports.
 */
const LOG_BUFFER_SIZE = 50;

export interface LogEntry {
  level: 'log' | 'warn' | 'error';
  message: string;
  stack?: string;
  timestamp: number;
}

const logBuffer: LogEntry[] = [];

let initialized = false;

export function initErrorLogger(): void {
  if (initialized) return;
  initialized = true;

  if (typeof window === 'undefined' || typeof console === 'undefined') return;

  const origLog = console.log;
  const origWarn = console.warn;
  const origError = console.error;

  console.log = (...args: unknown[]) => {
    pushLog('log', args);
    origLog(...args);
  };
  console.warn = (...args: unknown[]) => {
    pushLog('warn', args);
    origWarn(...args);
  };
  console.error = (...args: unknown[]) => {
    pushLog('error', args);
    origError(...args);
    if (typeof __IS_BETA__ !== 'undefined' && __IS_BETA__) {
      void submitErrorLog({
        source: 'console.error',
        message: args.map((a) => safeArgToString(a)).join(' '),
      });
    }
  };

  window.addEventListener('error', (e) => {
    void submitErrorLog({
      source: 'window-error',
      message: e.message,
      filename: e.filename,
      line: e.lineno,
      stack: (e as ErrorEvent & { error?: Error }).error?.stack,
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    void submitErrorLog({
      source: 'unhandled-rejection',
      reason: String(reason),
      stack: (reason as Error | undefined)?.stack,
    });
  });
}

/** Internal — exported only for tests. */
export function pushLog(level: LogEntry['level'], args: unknown[]): void {
  const entry: LogEntry = {
    level,
    message: args.map((a) => safeArgToString(a)).join(' '),
    stack: new Error().stack,
    timestamp: Date.now(),
  };
  logBuffer.push(entry);
  if (logBuffer.length > LOG_BUFFER_SIZE) logBuffer.shift();
}

/** Internal — exported only for tests. */
export function _resetLogBufferForTest(): void {
  logBuffer.length = 0;
}

function safeArgToString(a: unknown): string {
  if (typeof a === 'string') return a;
  return safeStringify(a, 500);
}

function safeStringify(obj: unknown, maxLen: number): string {
  try {
    return JSON.stringify(obj).slice(0, maxLen);
  } catch {
    return '[Unstringifiable]';
  }
}

export function getRecentLogs(): LogEntry[] {
  return [...logBuffer];
}

async function submitErrorLog(data: Record<string, unknown>): Promise<void> {
  // Niemals werfen — Logger darf das App-Verhalten nicht beeinflussen.
  try {
    await fetch('/api/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'auto-error',
        ...data,
        version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev',
        gitHash: typeof __GIT_HASH__ !== 'undefined' ? __GIT_HASH__ : 'dev',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        timestamp: Date.now(),
      }),
    });
  } catch {
    // Fail silent.
  }
}

export interface BugReportInput {
  pseudonym: string;
  expected: string;
  actual: string;
  steps?: string;
  includeGameState: boolean;
  includeLogs: boolean;
}

export interface BugReportResponse {
  trackingId: string;
}

/**
 * User-getriggerter Bug-Report. Schickt POST an `/api/log` mit Type `bug-report`,
 * inkl. Game-State-Snapshot und Recent-Logs (sofern Toggles aktiv).
 */
export async function submitBugReport(report: BugReportInput): Promise<BugReportResponse> {
  const payload = {
    type: 'bug-report',
    pseudonym: report.pseudonym,
    expected: report.expected,
    actual: report.actual,
    steps: report.steps ?? null,
    gameStateSnapshot: report.includeGameState ? getGameStateSnapshot() : null,
    recentLogs: report.includeLogs ? getRecentLogs() : null,
    version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev',
    gitHash: typeof __GIT_HASH__ !== 'undefined' ? __GIT_HASH__ : 'dev',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    viewport:
      typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '',
    timestamp: Date.now(),
  };

  const res = await fetch('/api/log', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!res.ok) throw new Error(`Bug-Report fehlgeschlagen (HTTP ${res.status})`);
  const json = (await res.json()) as { trackingId?: string };
  return { trackingId: json.trackingId ?? 'unknown' };
}

function getGameStateSnapshot(): Record<string, unknown> {
  try {
    const state = useGameStore.getState();
    return {
      mode: state.mode,
      homeCity: state.homeCity ?? null,
      credits: state.credits,
      level: state.level,
      stationCount: state.stations.length,
      vehicleCount: state.vehicles.length,
      activeMissions: state.missions.length,
      gameTime: state.gameTime,
      activeSlotId: state.activeSlotId,
    };
  } catch {
    return { snapshot: 'unavailable' };
  }
}

/* ──────────────────────────────────────────────────────────────────────── */
/*  Pseudonym helpers — used by BugReportModal + Migration v14→v15.         */
/* ──────────────────────────────────────────────────────────────────────── */

const PSEUDONYM_LS_KEY = 'testerPseudonym';

export function getOrCreateTesterPseudonym(): string {
  if (typeof window === 'undefined') return generatePseudonym();
  try {
    const stored = window.localStorage.getItem(PSEUDONYM_LS_KEY);
    if (stored && stored.length > 0) return stored;
    const fresh = generatePseudonym();
    window.localStorage.setItem(PSEUDONYM_LS_KEY, fresh);
    return fresh;
  } catch {
    return generatePseudonym();
  }
}

export function setTesterPseudonym(name: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PSEUDONYM_LS_KEY, name);
  } catch {
    // ignore
  }
}

export function generatePseudonym(): string {
  const hex = Math.floor(Math.random() * 0xffff)
    .toString(16)
    .toUpperCase()
    .padStart(4, '0');
  return `Anonym-${hex}`;
}
